/**
 * LanguagePicker — preferred-language selector and language-practice
 * mode toggle.
 *
 * Two grouped radio fieldsets:
 *   1. Preferred language: TR / EN / EL.
 *   2. Practice mode: off / English / Greek (Requirement 3.4).
 *
 * Selecting a preferred language persists the preference through
 * `usePreferencesStore.setLanguage` AND immediately swaps the active
 * i18next bundle via `changeLanguage` so the UI reflects the new
 * locale without a reload (Requirement 13.2). Practice mode changes
 * persist instantly through `setPracticeMode`.
 *
 * Validates: Requirements 3.3, 3.4, 13.1, 13.2.
 */

import type { ChangeEvent, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { changeLanguage } from '../../domain/i18n';
import type { LanguageCode, PracticeMode } from '../../domain/types';
import { usePreferencesStore } from '../../state';

import styles from './LanguagePicker.module.css';

export function LanguagePicker(): ReactElement {
  const { t } = useTranslation();
  const prefs = usePreferencesStore((s) => s.preferences);
  const setLanguage = usePreferencesStore((s) => s.setLanguage);
  const setPracticeMode = usePreferencesStore((s) => s.setPracticeMode);

  const handleLanguage = (lng: LanguageCode): void => {
    void (async (): Promise<void> => {
      // Persist first, then swap the active i18next bundle so the
      // visible UI labels follow the saved preference. `changeLanguage`
      // lazy-loads the locale bundle on first switch and is idempotent
      // thereafter.
      await setLanguage(lng);
      await changeLanguage(lng);
    })();
  };

  const handlePractice = (mode: PracticeMode): void => {
    void setPracticeMode(mode);
  };

  return (
    <section
      className={styles.section}
      aria-labelledby="lang-heading"
      data-testid="language-picker"
    >
      <h2 id="lang-heading" className={styles.heading}>
        {t('settings.language.title')}
      </h2>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{t('settings.language.preferred')}</legend>
        <Radio
          name="lang"
          value="tr"
          checked={prefs.language === 'tr'}
          onChange={() => handleLanguage('tr')}
        >
          Türkçe
        </Radio>
        <Radio
          name="lang"
          value="en"
          checked={prefs.language === 'en'}
          onChange={() => handleLanguage('en')}
        >
          English
        </Radio>
        <Radio
          name="lang"
          value="el"
          checked={prefs.language === 'el'}
          onChange={() => handleLanguage('el')}
        >
          Ελληνικά
        </Radio>
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>
          {t('settings.language.practiceMode')}
        </legend>
        <Radio
          name="practice"
          value="off"
          checked={prefs.practiceMode === 'off'}
          onChange={() => handlePractice('off')}
        >
          {t('settings.language.practiceOff')}
        </Radio>
        <Radio
          name="practice"
          value="en"
          checked={prefs.practiceMode === 'en'}
          onChange={() => handlePractice('en')}
        >
          {t('settings.language.practiceEn')}
        </Radio>
        <Radio
          name="practice"
          value="el"
          checked={prefs.practiceMode === 'el'}
          onChange={() => handlePractice('el')}
        >
          {t('settings.language.practiceEl')}
        </Radio>
      </fieldset>
    </section>
  );
}

interface RadioProps {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
}

function Radio({
  name,
  value,
  checked,
  onChange,
  children,
}: RadioProps): ReactElement {
  // The `onChange` shim collapses React's synthetic event into the
  // void-returning callback the parent expects, while keeping the
  // native `onChange` semantics so screen readers announce selection.
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.checked) onChange();
  };
  return (
    <label className={styles.radio}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={handleChange}
        className={styles.radioInput}
      />
      <span className={styles.radioLabel}>{children}</span>
    </label>
  );
}
